import React from 'react';
import { LegalLayout, H2, P, UL, Note } from './LegalLayout';
import { COMPANY } from './company';

const Terms: React.FC = () => (
  <LegalLayout title="Terms of Service" lastUpdated="7 September 2026">
    <P>
      These Terms of Service ("Terms") govern your access to and use of {COMPANY.appName}, a
      platform that lets golfers organise friendly wagers on rounds of golf and settle them
      through an in-app wallet (the "Service"). The Service is operated by {COMPANY.legalEntity}
      {COMPANY.registrationNumber ? ` (registration number ${COMPANY.registrationNumber})` : ''}
      {' '}("we", "us", "our"). By creating an account or using the Service, you agree to these Terms.
      If you do not agree, do not use the Service.
    </P>

    <Note>
      {COMPANY.appName} facilitates wagers between players. It is not a bookmaker, casino or
      lottery, and does not set odds, act as a counterparty to your bets, or guarantee any
      outcome. You are responsible for ensuring that participating in real-money wagers is lawful
      where you live.
    </Note>

    <H2>1. Eligibility</H2>
    <P>To use the Service you must:</P>
    <UL>
      <li>be at least {COMPANY.minAge} years old;</li>
      <li>have the legal capacity to enter into a binding contract;</li>
      <li>use the Service only where doing so is legal; and</li>
      <li>provide accurate registration information and keep it up to date.</li>
    </UL>
    <P>
      We may ask you to verify your identity or age at any time and may suspend accounts that do
      not, or that we reasonably believe are operated by anyone under {COMPANY.minAge}.
    </P>

    <H2>2. Your account</H2>
    <UL>
      <li>You are responsible for keeping your login credentials secure and for all activity under your account.</li>
      <li>You may sign in with an email and password or with a third-party provider (such as Google).</li>
      <li>One person, one account. Do not share, sell or transfer your account.</li>
      <li>Tell us immediately if you suspect unauthorised access.</li>
    </UL>

    <H2>3. How wagers work</H2>
    <P>
      Games and bets are created and agreed between players. When you join a bet, the stake is
      reserved from your wallet. On settlement, funds move between the participants' wallets
      according to the agreed result. {COMPANY.appName} provides the tools to record scores,
      agree outcomes and move funds — it does not decide who wins beyond applying the rules and
      results the players enter.
    </P>
    <UL>
      <li>Stakes are subject to minimum and maximum limits shown in the app.</li>
      <li>Disputed results follow the in-app settlement and dispute process. Where a proposal is left unanswered beyond the stated grace period, and no one has rejected it, it may be finalised automatically.</li>
      <li>You must not manipulate scores, collude, or otherwise act dishonestly. Doing so may result in reversal of transactions, suspension and forfeiture.</li>
    </UL>

    <H2>4. Wallet, deposits and withdrawals</H2>
    <UL>
      <li>The wallet holds a balance in South African Rand (ZAR) for use within the Service. It is not a bank account and earns no interest.</li>
      <li>Deposits are processed by our third-party payment provider (PayFast). We do not store your card details.</li>
      <li>Withdrawals are paid to a verified account you nominate and may be subject to identity and anti-fraud checks before release.</li>
      <li>We may set minimum/maximum deposit and withdrawal amounts and may decline or reverse transactions we reasonably believe are fraudulent, unlawful, or in breach of these Terms.</li>
    </UL>

    <H2>5. Fees</H2>
    <P>
      {COMPANY.appName} charges a small service fee on deposits to fund and run the platform.
      An estimated payment-gateway processing cost may also be added. All fees are shown to you
      before you confirm a deposit, and the amount credited to your wallet is the clean deposit
      amount (the fees are added on top). We may change our fees from time to time; changes apply
      to transactions made after the change.
    </P>

    <H2>6. Responsible play</H2>
    <P>
      Wagering should be fun and affordable. Only stake what you can comfortably afford to lose.
      If gambling stops being fun, please seek help — in South Africa you can contact the National
      Responsible Gambling Programme on 0800 006 008. You may ask us to limit or close your
      account at any time.
    </P>

    <H2>7. Prohibited conduct</H2>
    <P>You agree not to:</P>
    <UL>
      <li>use the Service for money laundering, fraud, or any unlawful purpose;</li>
      <li>cheat, collude, enter false scores, or use bots or automated means;</li>
      <li>create multiple or fake accounts, or impersonate others;</li>
      <li>attempt to gain unauthorised access to the Service or interfere with its operation; or</li>
      <li>use the Service if you are located where its use is illegal.</li>
    </UL>

    <H2>8. Suspension and termination</H2>
    <P>
      We may suspend or close your account, withhold funds pending investigation, or reverse
      transactions if we reasonably believe you have breached these Terms or the law. You may
      close your account at any time; any legitimate remaining balance will be returned to you,
      subject to applicable checks and fees.
    </P>

    <H2>9. Availability and changes</H2>
    <P>
      We aim to keep the Service available but do not guarantee it will be uninterrupted or
      error-free. We may modify, suspend or discontinue features at any time. We may also update
      these Terms; if we make material changes we will take reasonable steps to notify you, and
      continued use after the changes take effect means you accept them.
    </P>

    <H2>10. Disclaimers and limitation of liability</H2>
    <P>
      To the maximum extent permitted by law, the Service is provided "as is" without warranties
      of any kind. We are not liable for losses arising from wagers between players, from your
      failure to keep your account secure, or from events beyond our reasonable control. Nothing
      in these Terms excludes liability that cannot lawfully be excluded, including under the
      Consumer Protection Act where it applies.
    </P>

    <H2>11. Indemnity</H2>
    <P>
      You agree to indemnify us against claims, losses and costs arising from your misuse of the
      Service or breach of these Terms or the law.
    </P>

    <H2>12. Governing law</H2>
    <P>
      These Terms are governed by the laws of {COMPANY.governingLaw}, and you submit to the
      jurisdiction of its courts.
    </P>

    <H2>13. Contact</H2>
    <P>
      Questions about these Terms? Email us at {COMPANY.contactEmail}
      {COMPANY.address ? `, or write to ${COMPANY.address}, ${COMPANY.country}` : ''}.
    </P>
  </LegalLayout>
);

export default Terms;
